// =====================================================================
// src/components/BusinessLogo.jsx
//
// Shared logo tile that renders an uploaded photo if available, falling
// back to the V1 emoji-on-category-gradient style otherwise.
//
// Used across all customer-facing surfaces that show a business avatar:
//   - BusinessProfile (hero + wall posts)
//   - BusinessDirectory (directory cards)
//   - Cart (top bar + confirm)
//   - ConnectList (rows)
//   - HomeConnectListV2 (horizontal scroller cards)
//   - OrderDetail (business header)
//   - MyOrders (history rows)
//
// Centralised so future changes (lazy loading, on-error fallback, sizing
// tweaks) happen in one place. Keeps the per-screen JSX terse.
//
// Props:
//   className  — the existing per-screen logo-tile class. Inherits its
//                width/height/border-radius from the screen's CSS.
//   imageUrl   — business.logoImageUrl if set, otherwise null/undefined.
//   emoji      — fallback emoji (string) when no photo.
//   bg         — background style for the emoji branch (CSS gradient string
//                from logoBgFromCategory or similar).
//   children   — optional badges/overlays (e.g. verified check, "Pending")
//                that should sit on top of the logo tile. Keeps existing
//                screens' badge logic intact without forking the component.
//
// Notes:
//   - Image styles are inline so adopting screens don't need a CSS file
//     change. `borderRadius: 'inherit'` makes the img match whatever shape
//     the parent tile already has.
//   - Empty alt — the business name renders next to the logo on every
//     surface, so the image is decorative. (a11y check: screen readers
//     already announce the business name.)
//   - `background: transparent` on the photo branch so the gradient
//     doesn't peek through if the image has any rounded-corner anti-
//     aliasing transparency.
// =====================================================================

import React from 'react';

export default function BusinessLogo({
  className,
  imageUrl,
  emoji,
  bg,
  children,
}) {
  if (imageUrl) {
    return (
      <div
        className={className}
        style={{ background: 'transparent', overflow: 'hidden' }}
      >
        <img
          src={imageUrl}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
            display: 'block',
          }}
        />
        {children}
      </div>
    );
  }
  return (
    <div className={className} style={{ background: bg }}>
      {emoji}
      {children}
    </div>
  );
}
